// src/pages/GaragePage.jsx
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSettings } from '../hooks/useSettings';
import { getVehicles, getDeadlines, getDocuments, isBolloEsente, daysDiff, scadStatus } from '../lib/dataService';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const CAT_CONFIG = {
  AUTO:  { icon: '🚗', label: 'Auto',  activeClass: '#185FA5', activeBg: '#e6f1fb' },
  MOTO:  { icon: '🏍', label: 'Moto',  activeClass: '#0F6E56', activeBg: '#e1f5ee' },
  ALTRO: { icon: '🚛', label: 'Altro', activeClass: '#854F0B', activeBg: '#faeeda' },
};

export default function GaragePage() {
  const { vehicleId: paramId } = useParams();
  const { profile, isAdmin } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [vehicles,  setVehicles]  = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [cat,       setCat]       = useState('AUTO');
  const [idx,       setIdx]       = useState(0);
  const [tab,       setTab]       = useState('garage'); // garage | scadenze | riepilogo

  useEffect(() => {
    if (!profile) return;
    getVehicles(profile).then(vv => { setVehicles(vv); setLoading(false); });
  }, [profile]);

  const filtered = vehicles.filter(v => v.categoria === cat);
  const current  = filtered[idx] || null;

  // Se arrivo da un link diretto (/garage/:id)
  useEffect(() => {
    if (!paramId || !vehicles.length) return;
    const v = vehicles.find(x => x.id === paramId);
    if (!v) return;
    setCat(v.categoria);
    const fi = vehicles.filter(x => x.categoria === v.categoria).findIndex(x => x.id === paramId);
    if (fi >= 0) setIdx(fi);
  }, [paramId, vehicles]);

  // Carica scadenze e documenti del veicolo corrente
  useEffect(() => {
    if (!current) return;
    getDeadlines(current.id).then(setDeadlines);
    getDocuments(current.id).then(setDocuments);
  }, [current?.id]);

  const selectCat = (c) => { setCat(c); setIdx(0); };

  const counts = Object.fromEntries(
    Object.keys(CAT_CONFIG).map(c => [c, vehicles.filter(v => v.categoria === c).length])
  );

  const fmtDate = (d) => {
    if (!d) return '—';
    const date = d?.toDate ? d.toDate() : new Date(d);
    return format(date, 'd MMMM yyyy', { locale: it });
  };

  const pillCls = (status) => ({
    ok:      { color: '#2e7d32', bg: '#f0fff4' },
    warn:    { color: '#e65100', bg: '#fff3e0' },
    danger:  { color: '#c62828', bg: '#ffebee' },
    none:    { color: '#999',    bg: '#f5f5f5' },
    esente:  { color: '#2e7d32', bg: '#f0fff4' },
  }[status] || { color: '#999', bg: '#f5f5f5' });

  const scadLabel = (d) => {
    if (!d) return 'Non impostata';
    const dd = daysDiff(d?.toDate ? d.toDate() : new Date(d));
    if (dd < 0)  return `Scaduta da ${Math.abs(dd)} gg`;
    if (dd === 0) return 'Scade oggi!';
    if (dd < 60) return `tra ${dd} giorni`;
    return fmtDate(d);
  };

  const tipoLabel = {
    bollo: 'Bollo',
    assicurazione: 'Assicurazione',
    collaudo: 'Collaudo / Revisione',
    messa_in_moto: 'Messa in moto',
    certificazione: 'Certificazione',
  };

  if (loading) return <div style={styles.loading}>Carico il garage…</div>;

  return (
    <div style={styles.page}>
      {/* TOP BAR */}
      <div style={styles.topbar}>
        <span style={styles.logo} onClick={() => navigate('/')}>🏎 <img src="/garagememo-logo.svg" alt="GarageMemo" style={{height: '60px'}} /></span>
        <div style={styles.nav}>
          {['garage','scadenze','riepilogo'].map(t => (
            <button
              key={t}
              style={{ ...styles.navBtn, ...(tab===t ? styles.navBtnActive : {}) }}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
        <div style={styles.topRight}>
          {isAdmin && (
            <button style={styles.adminBtn} onClick={() => navigate('/admin')}>
              ⚙ Pannello admin
            </button>
          )}
        </div>
      </div>

      {/* CATEGORY BAR — solo nel garage */}
      {tab === 'garage' && (
        <div style={styles.catBar}>
          {Object.entries(CAT_CONFIG).map(([c, cfg]) => {
            const active = cat === c;
            return (
              <button
                key={c}
                style={{
                  ...styles.catBtn,
                  borderColor: active ? cfg.activeClass : '#e0e0e0',
                  background:  active ? cfg.activeBg    : '#fff',
                }}
                onClick={() => selectCat(c)}
              >
                <span style={styles.catIcon}>{cfg.icon}</span>
                <span style={styles.catLabel}>{cfg.label}</span>
                <span style={styles.catCount}>{counts[c]} veicoli</span>
              </button>
            );
          })}
        </div>
      )}

      <div style={styles.content}>
        {tab === 'garage' && (
          <>
            {/* NAVIGATION */}
            <div style={styles.vehNav}>
              <span style={styles.counter}>
                {filtered.length ? `${idx+1} di ${filtered.length}` : '—'}
              </span>
              <div style={styles.arrows}>
                <button style={styles.arrBtn} disabled={idx===0} onClick={() => setIdx(i => i-1)}>←</button>
                <button style={styles.arrBtn} disabled={idx>=filtered.length-1} onClick={() => setIdx(i => i+1)}>→</button>
              </div>
            </div>

            {/* VEHICLE CARD */}
            {current ? (
              <VehicleCard
                v={current}
                deadlines={deadlines.filter(d => settings?.tipi?.[d.tipo]?.attivo !== false)}
                documents={documents}
                isBolloEsente={isBolloEsente}
                fmtDate={fmtDate}
                scadLabel={scadLabel}
                pillCls={pillCls}
                tipoLabel={tipoLabel}
                isAdmin={isAdmin}
                onEdit={() => navigate(`/garage/${current.id}/edit`)}
                onAddDeadline={() => navigate(`/garage/${current.id}/scadenza`)}
              />
            ) : (
              <div style={styles.empty}>Nessun veicolo in questa categoria.</div>
            )}
          </>
        )}

        {tab === 'scadenze' && (
          <ScadenzeTab
            vehicles={vehicles}
            settings={settings}
            isBolloEsente={isBolloEsente}
            daysDiff={daysDiff}
            scadLabel={scadLabel}
            fmtDate={fmtDate}
            tipoLabel={tipoLabel}
            pillCls={pillCls}
            onVehicleClick={(vid) => {
              const v = vehicles.find(x => x.id === vid);
              if (v) { setCat(v.categoria); const fi=vehicles.filter(x=>x.categoria===v.categoria).findIndex(x=>x.id===vid); setIdx(fi>=0?fi:0); setTab('garage'); }
            }}
          />
        )}

        {tab === 'riepilogo' && (
          <RiepilogoTab vehicles={vehicles} isBolloEsente={isBolloEsente} />
        )}
      </div>
    </div>
  );
}

// ── VEHICLE CARD ─────────────────────────────────────────
function VehicleCard({ v, deadlines, documents, isBolloEsente, fmtDate, scadLabel, pillCls, tipoLabel, isAdmin, onEdit, onAddDeadline }) {
  const esente = isBolloEsente(v.annoImmatricolazione);
  const catIcons = { AUTO: '🚗', MOTO: '🏍', ALTRO: '🚛' };
  const catBgs   = { AUTO: '#e6f1fb', MOTO: '#e1f5ee', ALTRO: '#faeeda' };
  const worstStatus = () => {
    const ss = deadlines.map(d => {
      if (d.tipo === 'bollo' && esente) return 'ok';
      const dd2 = daysDiff(d.dataScadenza?.toDate?.() || d.dataScadenza);
      if (dd2 === null) return 'none';
      if (dd2 < 0) return 'danger';
      if (dd2 < 60) return 'warn';
      return 'ok';
    });
    if (ss.includes('danger')) return 'danger';
    if (ss.includes('warn')) return 'warn';
    return '';
  };
  const ws = worstStatus();
  const borderTop = ws === 'danger' ? '4px solid #c62828' : ws === 'warn' ? '4px solid #e65100' : '1px solid #e8e8e8';

  return (
    <div style={{ ...styles.card, borderTop }}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroTop}>
          <div style={{ ...styles.vehIcon, background: catBgs[v.categoria] }}>
            {catIcons[v.categoria] || '🚗'}
          </div>
          <div>
            <div style={styles.vehName}>{v.marca} {v.modello}</div>
            <div style={styles.vehSub}>{v.categoria}{v.annoImmatricolazione ? ` · ${v.annoImmatricolazione}` : ''}</div>
          </div>
        </div>
        <div style={styles.tags}>
          {v.targa
            ? <span style={styles.tagTarga}>{v.targa}</span>
            : <span style={{...styles.tag, background:'#ffebee', color:'#c62828'}}>Targa n.d.</span>}
          {v.certificazione && <span style={{...styles.tag, background:'#fff3e0', color:'#e65100'}}>{v.certificazione}</span>}
          {esente && <span style={{...styles.tag, background:'#f0fff4', color:'#2e7d32'}}>Esente bollo</span>}
          {v.proprietari?.map(p => <span key={p} style={{...styles.tag, background:'#e6f1fb', color:'#185FA5'}}>{p}</span>)}
        </div>
      </div>

      {/* Scadenze */}
      <div style={styles.section}>
        <div style={styles.secTitle}>Scadenze</div>
        {esente && (
          <div style={styles.scadRow}>
            <div>
              <div style={styles.scadName}>Bollo</div>
              <div style={styles.scadDate}>Immatricolato {v.annoImmatricolazione} — esente dopo 30 anni</div>
            </div>
            <Pill label="Esente" color="#2e7d32" bg="#f0fff4" />
          </div>
        )}
        {deadlines.length === 0 && !esente && (
          <div style={{ color: '#bbb', fontSize: 14, padding: '10px 0' }}>Nessuna scadenza registrata.</div>
        )}
        {deadlines.map(d => {
          if (d.tipo === 'bollo' && esente) return null;
          const dateVal = d.dataScadenza?.toDate ? d.dataScadenza.toDate() : d.dataScadenza ? new Date(d.dataScadenza) : null;
          const dd2 = daysDiff(dateVal);
          const st = dd2 === null ? 'none' : dd2 < 0 ? 'danger' : dd2 < 60 ? 'warn' : 'ok';
          const c = pillCls(st);
          return (
            <div key={d.id} style={styles.scadRow}>
              <div>
                <div style={styles.scadName}>{tipoLabel[d.tipo] || d.tipo}</div>
                <div style={styles.scadDate}>{dateVal ? fmtDate(dateVal) : '—'}{d.note ? ` · ${d.note}` : ''}</div>
              </div>
              <Pill label={scadLabel(d.dataScadenza)} color={c.color} bg={c.bg} />
            </div>
          );
        })}
        <button style={styles.addBtn} onClick={onAddDeadline}>+ Registra scadenza</button>
      </div>

      {/* Documenti */}
      <div style={styles.section}>
        <div style={styles.secTitle}>Documenti</div>
        <DocRow icon="📄" nome="Libretto / carta circolazione" ok={v.libretto} note={v.libretto ? 'Presente' : '⚠ Mancante — da reperire'} />
        <DocRow icon="📋" nome="Polizza assicurativa" ok={!!v.assicuratore} note={v.assicuratore ? `Compagnia: ${v.assicuratore}` : 'Non inserita'} />
        <DocRow icon="🏅" nome="Certificazione" ok={!!v.certificazione} note={v.certificazione || 'Nessuna'} />
        {documents.map(d => (
          <DocRow key={d.id} icon="📎" nome={d.nome} ok={true} note={d.tipo} url={d.url} />
        ))}
        {(isAdmin) && (
          <button style={styles.addBtn}>+ Allega documento / foto</button>
        )}
      </div>

      {isAdmin && (
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f0f0f0' }}>
          <button style={styles.editBtn} onClick={onEdit}>✏ Modifica veicolo</button>
        </div>
      )}
    </div>
  );
}

function Pill({ label, color, bg }) {
  return (
    <span style={{ padding: '5px 14px', borderRadius: 20, fontSize: 14, fontWeight: 600, color, background: bg, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {label}
    </span>
  );
}

function DocRow({ icon, nome, ok, note, url }) {
  return (
    <div style={{ ...styles.docRow, background: ok ? '#fff' : '#fff0f0', borderColor: ok ? '#e8e8e8' : '#ffcdd2' }}>
      <span style={{ fontSize: 22, width: 36, textAlign: 'center' }}>{icon}</span>
      <div>
        <div style={{ fontSize: 16 }}>{url ? <a href={url} target="_blank" rel="noreferrer" style={{ color: '#185FA5' }}>{nome}</a> : nome}</div>
        <div style={{ fontSize: 13, color: ok ? '#999' : '#c62828', marginTop: 2 }}>{note}</div>
      </div>
    </div>
  );
}

// ── SCADENZE TAB ─────────────────────────────────────────
function ScadenzeTab({ vehicles, settings, isBolloEsente, daysDiff, scadLabel, fmtDate, tipoLabel, pillCls, onVehicleClick }) {
  const [allDeadlines, setAllDeadlines] = useState([]);
  useEffect(() => {
    Promise.all(vehicles.map(v => getDeadlines(v.id).then(dd => dd.map(d => ({ ...d, veicolo: v })))))
      .then(res => setAllDeadlines(res.flat()));
  }, [vehicles]);

  const items = allDeadlines
    .filter(d => {
      if (settings?.tipi?.[d.tipo]?.attivo === false) return false;
      if (d.tipo === 'bollo' && isBolloEsente(d.veicolo.annoImmatricolazione)) return false;
      return true;
    })
    .map(d => {
      const dateVal = d.dataScadenza?.toDate ? d.dataScadenza.toDate() : d.dataScadenza ? new Date(d.dataScadenza) : null;
      return { ...d, dd: daysDiff(dateVal), dateVal };
    })
    .sort((a, b) => (a.dd ?? 9999) - (b.dd ?? 9999));

  const scadute = items.filter(i => i.dd !== null && i.dd < 0);
  const urgenti = items.filter(i => i.dd !== null && i.dd >= 0 && i.dd < 60);
  const future  = items.filter(i => i.dd === null || i.dd >= 60);

  const renderItem = (d) => {
    const dd2 = d.dd;
    const st = dd2 === null ? 'none' : dd2 < 0 ? 'danger' : dd2 < 60 ? 'warn' : 'ok';
    const c = pillCls(st);
    return (
      <div key={d.id} style={{ ...styles.scadTabItem, cursor: 'pointer' }} onClick={() => onVehicleClick(d.veicolo.id)}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{d.veicolo.marca} {d.veicolo.modello}</div>
          <div style={{ fontSize: 14, color: '#888', marginTop: 3 }}>
            {tipoLabel[d.tipo] || d.tipo} · {d.veicolo.targa || '—'} · {d.veicolo.proprietari?.join(', ')}
          </div>
        </div>
        <Pill label={scadLabel(d.dataScadenza)} color={c.color} bg={c.bg} />
      </div>
    );
  };

  return (
    <div>
      {scadute.length > 0 && <><div style={styles.secHead2}>🔴 Scadute</div>{scadute.map(renderItem)}</>}
      {urgenti.length > 0 && <><div style={styles.secHead2}>🟠 Entro 60 giorni</div>{urgenti.map(renderItem)}</>}
      {future.length  > 0 && <><div style={styles.secHead2}>Scadenze future</div>{future.map(renderItem)}</>}
      {items.length   === 0 && <div style={styles.empty}>Nessuna scadenza registrata.</div>}
    </div>
  );
}

// ── RIEPILOGO TAB ─────────────────────────────────────────
function RiepilogoTab({ vehicles, isBolloEsente }) {
  const totale = vehicles.length;
  const auto   = vehicles.filter(v => v.categoria === 'AUTO').length;
  const moto   = vehicles.filter(v => v.categoria === 'MOTO').length;
  const altro  = vehicles.filter(v => v.categoria === 'ALTRO').length;
  const senza  = vehicles.filter(v => !v.libretto).length;
  const esenti = vehicles.filter(v => isBolloEsente(v.annoImmatricolazione)).length;

  // Proprietari unici
  const propMap = {};
  vehicles.forEach(v => (v.proprietari || []).forEach(p => { propMap[p] = (propMap[p] || 0) + 1; }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, marginBottom: 20 }}>
        {[['🚗 Auto',auto,'#185FA5'],['🏍 Moto',moto,'#0F6E56'],['⚠ Libretti mancanti',senza,'#c62828'],['🟢 Esenti bollo',esenti,'#2e7d32'],['📦 Totale veicoli',totale,'#333']].map(([l,v,c]) => (
          <div key={l} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: '16px 18px', background: '#fff' }}>
            <div style={{ fontSize: 30, fontWeight: 600, color: c }}>{v}</div>
            <div style={{ fontSize: 14, color: '#888', marginTop: 3 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={styles.secHead2}>Veicoli per proprietario</div>
      {Object.entries(propMap).map(([p, n]) => (
        <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 15, width: 90 }}>{p}</span>
          <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 18, overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(n/totale*100)}%`, background: '#185FA5', height: '100%', borderRadius: 4, opacity: .75 }} />
          </div>
          <span style={{ fontSize: 15, color: '#888', width: 24, textAlign: 'right' }}>{n}</span>
        </div>
      ))}
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────
const styles = {
  page:    { minHeight: '100vh', background: '#f0f2f5', fontFamily: "'Georgia', serif" },
  loading: { padding: 40, textAlign: 'center', fontSize: 18, color: '#888' },
  topbar:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: '#fff', borderBottom: '1px solid #e8e8e8', flexWrap: 'wrap', gap: 8 },
  logo:    { fontSize: 18, fontWeight: 600, cursor: 'pointer' },
  nav:     { display: 'flex', gap: 4 },
  navBtn:  { background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', borderRadius: 8, fontSize: 15, color: '#888', fontFamily: 'inherit' },
  navBtnActive: { background: '#f0f0f0', color: '#1a1a1a', fontWeight: 600 },
  topRight:{ display: 'flex', alignItems: 'center', gap: 8 },
  adminBtn:{ background: 'none', border: '1px solid #e0e0e0', padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  catBar:  { display: 'flex', gap: 10, padding: '16px 20px', borderBottom: '1px solid #e8e8e8', background: '#fff' },
  catBtn:  { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 10px', borderRadius: 14, border: '2px solid', cursor: 'pointer', fontFamily: 'inherit', background: '#fff', transition: 'all .15s' },
  catIcon: { fontSize: 28, lineHeight: 1 },
  catLabel:{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  catCount:{ fontSize: 13, color: '#888' },
  content: { padding: 20, maxWidth: 700, margin: '0 auto' },
  vehNav:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  counter: { fontSize: 16, color: '#888' },
  arrows:  { display: 'flex', gap: 8 },
  arrBtn:  { width: 48, height: 48, borderRadius: 10, border: '1px solid #e0e0e0', background: '#f5f5f5', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  card:    { border: '1px solid #e8e8e8', borderRadius: 16, overflow: 'hidden', background: '#fff' },
  hero:    { padding: '24px 24px 20px', borderBottom: '1px solid #f0f0f0' },
  heroTop: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  vehIcon: { fontSize: 38, width: 64, height: 64, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  vehName: { fontSize: 26, fontWeight: 600, lineHeight: 1.2, color: '#1a1a1a' },
  vehSub:  { fontSize: 16, color: '#888', marginTop: 4 },
  tags:    { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tag:     { padding: '5px 12px', borderRadius: 8, fontSize: 14, fontWeight: 500 },
  tagTarga:{ padding: '5px 12px', borderRadius: 8, fontSize: 14, fontWeight: 500, background: '#f5f5f5', color: '#333', fontFamily: 'monospace', letterSpacing: 1, border: '1px solid #e0e0e0' },
  section: { padding: '20px 24px', borderBottom: '1px solid #f0f0f0' },
  secTitle:{ fontSize: 13, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 14 },
  scadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 10, background: '#fafafa', marginBottom: 8 },
  scadName:{ fontSize: 16, fontWeight: 600, color: '#1a1a1a' },
  scadDate:{ fontSize: 14, color: '#888', marginTop: 3 },
  docRow:  { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, border: '1px solid', marginBottom: 8 },
  addBtn:  { background: 'none', border: '1px dashed #ccc', borderRadius: 8, padding: '10px 16px', fontSize: 14, color: '#888', cursor: 'pointer', width: '100%', fontFamily: 'inherit', marginTop: 8 },
  editBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  empty:   { textAlign: 'center', padding: '60px 20px', color: '#bbb', fontSize: 17 },
  secHead2:{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.7px', margin: '20px 0 10px', paddingBottom: 6, borderBottom: '1px solid #f0f0f0' },
  scadTabItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderRadius: 12, border: '1px solid #e8e8e8', background: '#fff', marginBottom: 10 },
};
